import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import MyPageBreadcrumb from '../../components/mypage/MyPageBreadcrumb';
import { getMyHelpDeskPosts } from '../../services/mypageApi';
import { HelpDeskPost } from '../../types/api/mypage';
import { useNavigate } from 'react-router-dom';

interface MyPageInquiryProps {}

const MyPageInquiry: React.FC<MyPageInquiryProps> = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [inquiries, setInquiries] = useState<HelpDeskPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  const pageSize = 5; // 페이지당 5개씩
  
  // 초기 문의글 데이터 가져오기
  useEffect(() => {
    const fetchInitialInquiries = async () => {
      try {
        setLoading(true);
        const data = await getMyHelpDeskPosts(0, pageSize);
        setInquiries(data.content);
        setHasMore(!data.last);
        setCurrentPage(0);
        setError(null);
      } catch (err) {
        /// console.error('문의글 조회 실패:', err);
        setError('문의글을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialInquiries();
  }, []);

  // 추가 문의글 데이터 가져오기 (무한 스크롤)
  const loadMoreInquiries = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const data = await getMyHelpDeskPosts(nextPage, pageSize);
      
      setInquiries(prev => [...prev, ...data.content]);
      setHasMore(!data.last);
      setCurrentPage(nextPage);
    } catch (err) {
      /// console.error('추가 문의글 조회 실패:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // 스크롤 이벤트 처리
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      
      // 하단에서 100px 전에 도달했을 때 로드
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMoreInquiries();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage, hasMore, loadingMore]);

  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}년 ${month}월 ${day}일 ${hours}:${minutes}`;
  };

  // 답변 상태 결정 (답글 수가 0이면 답변 대기, 1 이상이면 답변 완료)
  const getStatusInfo = (replyCnt: number) => {
    if (replyCnt > 0) {
      return {
        status: '답변 완료',
        statusType: 'completed' as const
      };
    }
    return {
      status: '답변 대기',
      statusType: 'pending' as const
    };
  };

  // 검색 필터링 (검색 시에는 무한 스크롤 비활성화)
  const filteredInquiries = searchQuery 
    ? inquiries.filter(inquiry =>
        inquiry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inquiry.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : inquiries;

  return (
    <MyPageLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>문의하기</PageTitle>
          </PageHeader>

          {/* 브레드크럼 */}
          <NavigationContainer>
            <MyPageBreadcrumb 
              items={[
                { label: '홈' },
                { label: '마이페이지' },
                { label: '문의하기' }
              ]}
            />
            
            {/* 검색바 */}
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="검색어를 입력해주세요."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchIcon>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M14.6667 14.6667L11.2 11.2M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z" stroke="rgba(55, 56, 60, 0.28)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SearchIcon>
            </SearchContainer>
          </NavigationContainer>

          {/* 안내 문구 */}
          <InfoSection>
            <InfoList>
              <InfoItem>투비닥터 캠퍼스의 운영에 관한 문의를 남기실 수 있는 공간입니다.</InfoItem>
              <InfoItem>개별 강의 내용에 관한 질문은 커뮤니티 내의 강의 질문 게시판을 이용해주세요.</InfoItem>
              <InfoItem>남겨주신 문의는 관리자가 확인 후 빠른 시일 내에 답변을 드립니다.</InfoItem>
            </InfoList>
          </InfoSection>

          {/* 문의 목록 */}
          <InquiryList>
            {loading ? (
              <LoadingContainer>
                <LoadingText>문의글을 불러오는 중...</LoadingText>
              </LoadingContainer>
            ) : error ? (
              <ErrorContainer>
                <ErrorText>{error}</ErrorText>
              </ErrorContainer>
            ) : filteredInquiries.length === 0 ? (
              <EmptyContainer>
                <EmptyText>
                  {searchQuery ? '검색 결과가 없습니다.' : '작성한 문의글이 없습니다.'}
                </EmptyText>
              </EmptyContainer>
            ) : (
              <>
                {filteredInquiries.map((inquiry) => {
                  const statusInfo = getStatusInfo(inquiry.reply_cnt);
                  return (
                    <InquiryItem key={inquiry.post_id} onClick={() => navigate(`/mypage/inquiry/post/${inquiry.post_id}`)}>
                      <InquiryContent>
                        <InquiryHeader>
                          <StatusBadge $statusType={statusInfo.statusType}>
                            {statusInfo.status}
                          </StatusBadge>
                          <InquiryTitle>{inquiry.title}</InquiryTitle>
                        </InquiryHeader>
                        
                        <InquiryMeta>
                          <AuthorInfo>
                            <ProfileImage>
                              <img src="/images/default-avatar.png" alt="프로필" />
                            </ProfileImage>
                            <AuthorName>{inquiry.writer_name}</AuthorName>
                            <Divider />
                            <PostDate>{formatDate(inquiry.created_at)}</PostDate>
                          </AuthorInfo>
                        </InquiryMeta>
                      </InquiryContent>
                      <InquiryDivider />
                    </InquiryItem>
                  );
                })}
                
                {/* 더 보기 로딩 상태 */}
                {loadingMore && (
                  <LoadMoreContainer>
                    <LoadingText>더 많은 문의글을 불러오는 중...</LoadingText>
                  </LoadMoreContainer>
                )}
                
                {/* 더 이상 불러올 데이터가 없을 때 */}
                {!hasMore && !searchQuery && inquiries.length > 0 && (
                  <EndContainer>
                    <EndText>모든 문의글을 확인했습니다.</EndText>
                  </EndContainer>
                )}
              </>
            )}
          </InquiryList>
        </MainContainer>
      </Container>
    </MyPageLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationContainer = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
`;

const SearchContainer = styled.div`
  width: 282px;
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 32px 8px 32px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 12px;
  background: white;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  outline: none;

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }

  &:focus {
    border-color: #448181;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const InfoSection = styled.div`
  width: 100%;
  padding: 32px;
  background: rgba(112, 115, 124, 0.08);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
`;

const InfoList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoItem = styled.li`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  position: relative;
  padding-left: 16px;
  
  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #171719;
    font-weight: bold;
  }
`;

const InquiryList = styled.div`
  width: 894px;
  display: flex;
  flex-direction: column;
`;

const InquiryItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  
  &:hover {
    cursor: pointer;
  }
`;

const InquiryContent = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InquiryHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.div<{ $statusType: 'pending' | 'completed' }>`
  display: inline-flex;
  height: 24px;
  padding: 0 6px;
  background: ${props => 
    props.$statusType === 'completed' 
      ? 'rgba(68, 129, 129, 0.08)' 
      : 'rgba(112, 115, 124, 0.08)'
  };
  border-radius: 6px;
  justify-content: center;
  align-items: center;
  align-self: flex-start;
  
  color: ${props => 
    props.$statusType === 'completed' 
      ? '#448181' 
      : 'rgba(55, 56, 60, 0.61)'
  };
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 16.01px;
  letter-spacing: 0.30px;
`;

const InquiryTitle = styled.div`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
`;

const InquiryMeta = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const AuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const ProfileImage = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 1px solid rgba(112, 115, 124, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.div`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const PostDate = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const InquiryDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingText = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const ErrorContainer = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const EmptyContainer = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmptyText = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const LoadMoreContainer = styled.div`
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(112, 115, 124, 0.04);
  border-radius: 8px;
  margin-top: 16px;
`;

const EndContainer = styled.div`
  width: 100%;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
`;

const EndText = styled.div`
  color: rgba(55, 56, 60, 0.41);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

export default MyPageInquiry;